import { isReference } from '@apollo/client';
import type { FetchResult, StoreObject } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import type { GetFhirRecordParams } from '@models/fhir-resource';
import { MedicalConditionRecord } from '@models/medical-condition';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import {
  useListConditionsQuery,
  useGetConditionQuery,
  usePostConditionRecordMutation,
  useUpdateConditionRecordMutation,
  useDeleteConditionRecordMutation,
} from 'generated/condition.generated';
import type {
  PostConditionRecordMutationVariables,
  UpdateConditionRecordMutationVariables,
  DeleteConditionRecordMutation,
  DeleteConditionRecordMutationVariables,
} from 'generated/condition.generated';
import type { GetPatientHealthSummaryRecordsQuery } from 'generated/patient-record.generated';
import { GetPatientHealthSummaryRecordsDocument } from 'generated/patient-record.generated';

export const useGetMedicalConditions = () => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListConditionsQuery({
    variables: {
      input: {
        patient: patient_id,
        count: 1500,
        page: 1,
      },
    },
    skip: !patient_id,
  });
  const { data, error } = response;
  handleError(error);
  const records = (data?.listConditions.data ?? []).map(
    (r) => new MedicalConditionRecord(r)
  );
  return { ...response, data: records };
};

export const useGetMedicalCondition = (params: GetFhirRecordParams) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const { record_id } = params;
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetConditionQuery({
    variables: {
      input: {
        ...params,
        patient_id,
      },
    },
    skip:
      !patient_id || !record_id || record_id === 'add' || Boolean(params.skip),
  });
  const { data, error } = response;
  handleError(error);
  const record = data?.getCondition;
  return {
    ...response,
    data: record ? new MedicalConditionRecord(record) : null,
  };
};

export function useCreateMedicalCondition(): [
  (
    params: PostConditionRecordMutationVariables
  ) => Promise<FetchResult<MedicalConditionRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = usePostConditionRecordMutation();
  const loading = baseLoading;

  async function createMedicalCondition(
    params: PostConditionRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postConditionRecord) {
          return;
        }
        const storeObject =
          response.postConditionRecord as unknown as StoreObject;
        const condition = params.input;
        try {
          cache.modify<{ listConditions: CachedRecordList }>({
            fields: {
              listConditions: (existing, { storeFieldName, toReference }) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === condition.subject.reference) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });

          cache.updateQuery<GetPatientHealthSummaryRecordsQuery>(
            {
              query: GetPatientHealthSummaryRecordsDocument,
              variables: {
                input: {
                  patient: condition.subject.reference,
                },
                condition: true,
              },
            },
            (data) => ({
              getPatientHealthSummaryRecords: {
                condition: [
                  ...(data?.getPatientHealthSummaryRecords.condition ?? []),
                  response.postConditionRecord,
                ],
              },
            })
          );
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const createdCondition = response.data?.postConditionRecord;
    return {
      ...response,
      data: createdCondition
        ? new MedicalConditionRecord(createdCondition)
        : undefined,
    };
  }
  return [createMedicalCondition, loading];
}

export function useUpdateMedicalCondition(): [
  (
    params: UpdateConditionRecordMutationVariables
  ) => Promise<FetchResult<MedicalConditionRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useUpdateConditionRecordMutation();
  const loading = baseLoading;

  async function updateMedicalCondition(
    params: UpdateConditionRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    const updatedCondition = response.data?.updateConditionRecord;
    return {
      ...response,
      data: updatedCondition
        ? new MedicalConditionRecord(updatedCondition)
        : undefined,
    };
  }
  return [updateMedicalCondition, loading];
}

export function useDeleteMedicalCondition(): [
  (
    params: DeleteConditionRecordMutationVariables
  ) => Promise<FetchResult<DeleteConditionRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useDeleteConditionRecordMutation();
  const loading = baseLoading;

  async function deleteMedicalCondition(
    params: DeleteConditionRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteConditionRecord) {
          return;
        }
        const { condition_id } = params.input;
        try {
          const normalizedId = cache.identify({
            id: condition_id,
            __typename: 'ConditionModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteMedicalCondition, loading];
}
